import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { runbookSources, slugOf } from "./runbooks";

/** An inline link in running text: a colour AND an underline, so it is not told apart by colour alone. */
const linkClass = "text-link underline underline-offset-4 hover:no-underline";

// A small Markdown renderer for the runbooks (CHT-772). It supports exactly what the runbooks use: headings, paragraphs, bullet and numbered
// lists, tables, fenced code blocks, `code`, **bold** and links. It builds React nodes from text only, so no HTML in a runbook can run (there
// is no `dangerouslySetInnerHTML`). A link to another runbook (`stuck-job.md#check`) becomes an in-app link; an `http(s)` link opens as a
// plain external link; any other link is shown as its text.

type Block =
  | Readonly<{ kind: "heading"; level: 1 | 2 | 3; text: string }>
  | Readonly<{ kind: "paragraph"; text: string }>
  | Readonly<{ kind: "list"; ordered: boolean; items: readonly string[] }>
  | Readonly<{ kind: "table"; head: readonly string[]; rows: readonly (readonly string[])[] }>
  | Readonly<{ kind: "code"; text: string }>;

const cells = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

export function parseMarkdown(source: string): readonly Block[] {
  const lines = source.split("\n");
  const blocks: Block[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index] as string;
    if (line.trim() === "") {
      index += 1;
      continue;
    }
    if (line.startsWith("```")) {
      const body: string[] = [];
      index += 1;
      while (index < lines.length && !(lines[index] as string).startsWith("```")) {
        body.push(lines[index] as string);
        index += 1;
      }
      index += 1;
      blocks.push({ kind: "code", text: body.join("\n") });
      continue;
    }
    const heading = /^(#{1,3}) +(.+?) *$/.exec(line);
    if (heading !== null) {
      blocks.push({
        kind: "heading",
        level: (heading[1] as string).length as 1 | 2 | 3,
        text: heading[2] as string,
      });
      index += 1;
      continue;
    }
    if (line.trimStart().startsWith("|") && /^ *\|? *:?-{3,}/.test(lines[index + 1] ?? "")) {
      const head = cells(line);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && (lines[index] as string).trimStart().startsWith("|")) {
        rows.push(cells(lines[index] as string));
        index += 1;
      }
      blocks.push({ kind: "table", head, rows });
      continue;
    }
    const bullet = /^ *[-*] +/;
    const numbered = /^ *\d+\. +/;
    if (bullet.test(line) || numbered.test(line)) {
      const ordered = numbered.test(line);
      const marker = ordered ? numbered : bullet;
      const items: string[] = [];
      while (index < lines.length) {
        const current = lines[index] as string;
        if (marker.test(current)) items.push(current.replace(marker, ""));
        else if (current.startsWith("   ") && items.length > 0 && current.trim() !== "") {
          // A continuation of the previous item, or a nested bullet shown as part of it.
          items[items.length - 1] =
            `${items[items.length - 1]} ${current.trim().replace(/^[-*] +/, "")}`;
        } else break;
        index += 1;
      }
      blocks.push({ kind: "list", ordered, items });
      continue;
    }
    // The first line is always taken: a line that starts with `|` but has no table rule under it is text, and without this it would match
    // no block and the loop would never move on.
    const paragraph: string[] = [line.trim()];
    index += 1;
    while (
      index < lines.length &&
      (lines[index] as string).trim() !== "" &&
      !/^(#{1,3} |```|\|)/.test(lines[index] as string) &&
      !/^ *([-*]|\d+\.) +/.test(lines[index] as string)
    ) {
      paragraph.push((lines[index] as string).trim());
      index += 1;
    }
    blocks.push({ kind: "paragraph", text: paragraph.join(" ") });
  }
  return blocks;
}

/** Keys for the parts of a static document: they come from the position, and the document never reorders. */
const keyed = <T,>(items: readonly T[], prefix: string): readonly (readonly [string, T])[] =>
  items.map((item, position) => [`${prefix}${position}`, item] as const);

const INLINE = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/;

function inline(text: string, keyPrefix: string): ReactNode[] {
  return text.split(INLINE).map((part, position) => {
    const key = `${keyPrefix}-${position}`;
    if (part.startsWith("`") && part.endsWith("`") && part.length > 1) {
      return <code key={key}>{part.slice(1, -1)}</code>;
    }
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (link !== null) {
      const label = link[1] as string;
      const target = link[2] as string;
      const runbook = /^([a-z-]+)\.md(?:#([a-z0-9-]+))?$/.exec(target);
      if (runbook !== null && runbookSources[runbook[1] as string] !== undefined) {
        const slug = runbook[1] === "README" ? "index" : (runbook[1] as string);
        return (
          <Link
            key={key}
            className={linkClass}
            to={`/operations/runbooks/${slug}${runbook[2] === undefined ? "" : `#${runbook[2]}`}`}
          >
            {label}
          </Link>
        );
      }
      if (/^https?:\/\//.test(target)) {
        return (
          <a
            key={key}
            className={linkClass}
            href={target}
            rel="noreferrer noopener"
            target="_blank"
          >
            {label}
          </a>
        );
      }
      return label;
    }
    return part;
  });
}

/**
 * The scroll box of a table. It marks the side that has more columns off screen with a 1px line (the same hint as `DataTable`), because a
 * border that is always there does not say which side hides something when the scrollbar is not shown.
 */
function TableBox({ label, children }: Readonly<{ label: string; children: ReactNode }>) {
  const box = useRef<HTMLElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  const measure = useCallback(() => {
    const node = box.current;
    if (node === null) return;
    const left = node.scrollLeft > 1;
    const right = node.scrollLeft + node.clientWidth < node.scrollWidth - 1;
    setEdges((previous) =>
      previous.left === left && previous.right === right ? previous : { left, right },
    );
  }, []);
  useEffect(() => {
    measure();
    const node = box.current;
    if (node === null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    if (node.firstElementChild) observer.observe(node.firstElementChild);
    return () => observer.disconnect();
  }, [measure]);
  return (
    <div className="relative">
      <section
        ref={box}
        onScroll={measure}
        // A box that scrolls must be reachable by keyboard (WCAG 2.1.1), and a region has a fixed name.
        aria-label={label}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be reachable by keyboard
        tabIndex={0}
        className="overflow-x-auto rounded-md border border-hairline"
      >
        {children}
      </section>
      {edges.right ? (
        <div
          aria-hidden="true"
          data-testid="scroll-hint-right"
          className="pointer-events-none absolute inset-y-0 right-0 w-px rounded-r-md bg-input"
        />
      ) : null}
      {edges.left ? (
        <div
          aria-hidden="true"
          data-testid="scroll-hint-left"
          className="pointer-events-none absolute inset-y-0 left-0 w-px rounded-l-md bg-input"
        />
      ) : null}
    </div>
  );
}

function renderBlock(block: Block, key: string, tableNumber: number): ReactNode {
  switch (block.kind) {
    case "heading": {
      const id = slugOf(block.text);
      const content = inline(block.text, key);
      return block.level === 1 ? (
        <h2 key={key} id={id}>
          {content}
        </h2>
      ) : block.level === 2 ? (
        <h3 key={key} id={id}>
          {content}
        </h3>
      ) : (
        <h4 key={key} id={id}>
          {content}
        </h4>
      );
    }
    case "paragraph":
      return <p key={key}>{inline(block.text, key)}</p>;
    case "code":
      return (
        <pre key={key}>
          <code>{block.text}</code>
        </pre>
      );
    case "list": {
      const items = keyed(block.items, `${key}-`).map(([itemKey, item]) => (
        <li key={itemKey}>{inline(item, itemKey)}</li>
      ));
      return block.ordered ? <ol key={key}>{items}</ol> : <ul key={key}>{items}</ul>;
    }
    case "table":
      return (
        <TableBox key={key} label={`Table ${tableNumber} of this runbook`}>
          <table className="w-full border-collapse text-sm leading-5 [&_code]:text-xs [&_td]:border-t [&_td]:border-hairline [&_td]:px-3.5 [&_td]:py-2.5 [&_td]:align-top [&_th]:bg-pill [&_th]:px-3.5 [&_th]:py-2.5 [&_th]:text-left [&_th]:align-top [&_th]:text-[13px] [&_th]:leading-[18px] [&_th]:font-semibold [&_th]:text-muted-foreground">
            <thead>
              <tr>
                {keyed(block.head, `${key}-h`).map(([cellKey, cell]) => (
                  <th key={cellKey} scope="col">
                    {inline(cell, cellKey)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keyed(block.rows, `${key}-r`).map(([rowKey, row]) => (
                <tr key={rowKey}>
                  {keyed(row, `${rowKey}-`).map(([cellKey, cell]) => (
                    <td key={cellKey}>{inline(cell, cellKey)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
      );
    default: {
      const unreachable: never = block;
      return unreachable;
    }
  }
}

/** The runbook as React nodes. A heading has the id of its anchor, so a link with `#section` lands on it. */
export function Markdown({ source }: Readonly<{ source: string }>) {
  let tables = 0;
  return (
    <>
      {keyed(parseMarkdown(source), "b").map(([key, block]) => {
        if (block.kind === "table") tables += 1;
        return renderBlock(block, key, tables);
      })}
    </>
  );
}
